// What the peek on an issue or pull request link shows, without opening it:
// its title, state, author, and (for a pull request) branches and size, and
// its description. One request per reference, asked for only when the link
// is hovered or focused, and kept for a while.
import type { RefState } from './refDetails';

export interface RefPeek {
  kind: 'pull' | 'issue';
  title: string;
  state: RefState;
  author: string | null;
  createdAt: string;
  /** The description as GitHub renders it, and its Markdown (for when GitHub gives no HTML). */
  bodyHTML: string;
  body: string;
  comments: number;
  pull: {
    base: string;
    head: string;
    additions: number;
    deletions: number;
    changedFiles: number;
    /** GitHub's `reviewDecision`: APPROVED, CHANGES_REQUESTED, REVIEW_REQUIRED, or null when none is required. */
    reviewDecision: string | null;
  } | null;
}

const COMMON = 'title state body bodyHTML createdAt author { login } comments { totalCount }';

export const REF_PEEK_QUERY = `query RefPeek($o: String!, $n: String!, $num: Int!) {
  repository(owner: $o, name: $n) {
    issueOrPullRequest(number: $num) {
      __typename
      ... on Issue { ${COMMON} }
      ... on PullRequest { ${COMMON} isDraft baseRefName headRefName additions deletions changedFiles reviewDecision }
    }
  }
}`;

interface Node {
  __typename?: string;
  title?: string;
  state?: 'OPEN' | 'CLOSED' | 'MERGED';
  isDraft?: boolean;
  body?: string | null;
  bodyHTML?: string | null;
  createdAt?: string;
  author?: { login: string } | null;
  comments?: { totalCount: number };
  baseRefName?: string;
  headRefName?: string;
  additions?: number;
  deletions?: number;
  changedFiles?: number;
  reviewDecision?: string | null;
}

/** The peek in an answer to REF_PEEK_QUERY: null when GitHub has no such issue or pull request (or the viewer cannot read it). */
export function readRefPeek(data: unknown): RefPeek | null {
  const node = (data as { repository?: { issueOrPullRequest?: Node | null } | null } | null)?.repository?.issueOrPullRequest;
  if (!node || (node.__typename !== 'Issue' && node.__typename !== 'PullRequest') || typeof node.title !== 'string') return null;
  const pull = node.__typename === 'PullRequest';
  const state: RefState = node.state === 'MERGED' ? 'merged' : node.state === 'CLOSED' ? 'closed' : pull && node.isDraft ? 'draft' : 'open';
  return {
    kind: pull ? 'pull' : 'issue',
    title: node.title,
    state,
    author: node.author?.login ?? null,
    createdAt: node.createdAt ?? '',
    bodyHTML: node.bodyHTML ?? '',
    body: node.body ?? '',
    comments: node.comments?.totalCount ?? 0,
    pull: pull
      ? {
          base: node.baseRefName ?? '',
          head: node.headRefName ?? '',
          additions: node.additions ?? 0,
          deletions: node.deletions ?? 0,
          changedFiles: node.changedFiles ?? 0,
          reviewDecision: node.reviewDecision ?? null,
        }
      : null,
  };
}

/** A review decision as the peek says it; null when there is nothing to say. */
export function reviewDecisionText(d: string | null): string | null {
  if (d === 'APPROVED') return 'approved';
  if (d === 'CHANGES_REQUESTED') return 'changes requested';
  if (d === 'REVIEW_REQUIRED') return 'review required';
  return null;
}
